import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllObjectsByClient, addObjectFromExisting } from '../utils/storage';
import { ModalTemplate } from './templates';
import './Modal.css';
import './SelectExistingObjectModal.css';

// Same identity as the Archived Projects tree: an object is its TYPE + ID
// combo within a test site; each archived row under it is a past project.
const objKey = (o) => `${o.type || ''} ${o.objectTag || ''}`;
const cmp = (a, b) => (a || '').localeCompare(b || '', undefined, { numeric: true, sensitivity: 'base' });

/**
 * SelectExistingObjectModal — shown before "New Object" so an object already
 * on file (active or archived) isn't created a second time.
 *
 * OK on an archived object → new active object copying its object-level info
 * (it files back under the same object in Archived Projects once archived).
 * OK on an active object → just opens it; it's already in progress.
 *
 * Props:
 *   clientId    – test site whose objects are listed
 *   onClose     – cancel / ✕
 *   onCreateNew – "Create New Object" → the normal New Object form
 *   onCreated   – after a copy was created
 */
export default function SelectExistingObjectModal({ clientId, onClose, onCreateNew, onCreated }) {
  const navigate = useNavigate();
  const [objects,  setObjects]  = useState(null);   // null = loading
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState('');     // objKey
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    let cancelled = false;
    getAllObjectsByClient(clientId)
      .then((list) => { if (!cancelled) setObjects(list); })
      .catch(() => { if (!cancelled) { setObjects([]); setLoadError('Could not load existing objects.'); } });
    return () => { cancelled = true; };
  }, [clientId]);

  // One entry per type + ID: the active object if there is one, else the
  // most recently archived project (its design spec is the latest).
  const entries = useMemo(() => {
    const groups = new Map();
    for (const o of objects || []) {
      const k = objKey(o);
      const cur = groups.get(k);
      const better = !cur
        || (!o.archivedAt && cur.archivedAt)
        || (o.archivedAt && cur.archivedAt && o.archivedAt > cur.archivedAt);
      if (better) groups.set(k, o);
    }
    return [...groups.entries()]
      .map(([key, o]) => ({ key, obj: o, active: !o.archivedAt }))
      .sort((a, b) => cmp(a.obj.type, b.obj.type) || cmp(a.obj.objectTag, b.obj.objectTag));
  }, [objects]);

  async function handleOk() {
    const entry = entries.find((e) => e.key === selected);
    if (!entry || saving) return;
    if (entry.active) {
      onClose();
      navigate(`/client/${clientId}/object/${entry.obj.id}`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await addObjectFromExisting(entry.obj);
      onCreated(created);
    } catch (err) {
      console.error('Failed to create object from existing:', err);
      setError('Failed to create the object. Please check your connection and try again.');
      setSaving(false);
    }
  }

  return (
    <ModalTemplate
      title="Select from Existing Objects"
      onClose={onClose}
      actionsSpread
      actions={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onCreateNew} disabled={saving}>
            Create New Object
          </button>
          <div className="modal-actions-right">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleOk} disabled={!selected || saving}>
              {saving ? 'Creating…' : 'OK'}
            </button>
          </div>
        </>
      )}
    >
      <p className="seo-hint">
        Check whether this object is already on file for this test site. Picking an archived
        object starts a new project for it; it is filed under the same object once archived.
      </p>
      <div className="seo-list" role="radiogroup" aria-label="Existing objects">
        {objects === null ? (
          <div className="seo-empty">Loading…</div>
        ) : loadError ? (
          <div className="seo-empty">{loadError}</div>
        ) : entries.length === 0 ? (
          <div className="seo-empty">No objects on file for this test site yet.</div>
        ) : entries.map(({ key, obj, active }) => (
          <label key={key} className={`seo-item${selected === key ? ' selected' : ''}`}>
            <input
              type="radio"
              name="existing-object"
              checked={selected === key}
              onChange={() => { setSelected(key); setError(null); }}
            />
            <span className="seo-label">{[obj.type, obj.objectTag].filter(Boolean).join(' - ') || '—'}</span>
            <span className={`seo-chip${active ? ' active' : ''}`}>{active ? 'Active' : 'Archived'}</span>
          </label>
        ))}
      </div>
      {error && <div className="alert alert-danger" style={{ marginTop: 12 }}>{error}</div>}
    </ModalTemplate>
  );
}
